import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";
import type { Unit } from "@/lib/types/unit";
import type { WeaponData } from "@/lib/types/weapon";
import { UnitStats } from "@/components/UnitDetail/UnitStats";
import { WeaponCard } from "@/components/UnitDetail/WeaponCard";
import { TraitBadgeList } from "@/components/ui/trait-badge-list";
import { getUnitImagePath } from "@/lib/utils/unit-images";

interface CompactUnitCardProps {
  unit: Unit | undefined;
  title: string;
  placeholder: string;
  weaponData?: { descriptorName: string; hasDefensiveSmoke: boolean; weapons: WeaponData[] } | null;
  loadingWeapons?: boolean;
}

export function CompactUnitCard({
  unit,
  title,
  placeholder,
  weaponData,
  loadingWeapons = false,
}: CompactUnitCardProps) {
  const [imageError, setImageError] = useState(false);
  const [activeTab, setActiveTab] = useState<"stats" | "weapons">("stats");
  const [activeWeaponIndex, setActiveWeaponIndex] = useState(0);

  useEffect(() => {
    setImageError(false);
  }, [unit?.descriptorName]);

  useEffect(() => {
    if (weaponData?.weapons && weaponData.weapons.length > 0) {
      setActiveWeaponIndex(0);
    }
  }, [weaponData?.weapons]);

  if (!unit) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold">{title}</CardTitle>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-center text-muted-foreground py-8">
            <div className="text-sm">{placeholder}</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const hasWeapons = weaponData?.weapons && weaponData.weapons.length > 0;

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-semibold">{title}</CardTitle>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col overflow-hidden">
        {/* Unit Image */}
        <div className="w-full h-32 flex items-center justify-center bg-muted/50 rounded-lg border-2 border-muted-foreground/10 overflow-hidden mb-3 flex-shrink-0">
          {!imageError ? (
            <img
              src={getUnitImagePath(unit.descriptorName)}
              alt={unit.name}
              className="w-full h-full max-h-32 object-contain"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="w-full h-32 bg-muted rounded-lg flex items-center justify-center border-2 border-dashed border-muted-foreground/20">
              <span className="text-xs text-muted-foreground">Unit Image</span>
            </div>
          )}
        </div>

        {/* Unit Name and Descriptor */}
        <div className="space-y-1 mb-3 flex-shrink-0">
          <div className="font-semibold text-base">{unit.name}</div>
          <div className="text-xs text-muted-foreground font-mono">
            {unit.descriptorName}
          </div>
        </div>

        {/* Specialities */}
        {unit.specialities && unit.specialities.length > 0 && (
          <div className="mb-3 flex-shrink-0">
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">
              Specialities
            </div>
            <TraitBadgeList specialities={unit.specialities} size="sm" />
          </div>
        )}

        {/* Unit Flags */}
        {(unit.isCommand || unit.isSpecialForces) && (
          <div className="flex flex-wrap gap-1.5 mb-3 flex-shrink-0">
            {unit.isCommand && (
              <Badge variant="outline" className="text-[10px]">Command</Badge>
            )}
            {unit.isSpecialForces && (
              <Badge variant="outline" className="text-[10px]">Special Forces</Badge>
            )}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 border-b mb-2 flex-shrink-0">
          <Button
            variant={activeTab === "stats" ? "default" : "ghost"}
            onClick={() => setActiveTab("stats")}
            className="rounded-b-none text-xs h-7 px-2"
            size="sm"
          >
            Stats
          </Button>
          {hasWeapons && (
            <Button
              variant={activeTab === "weapons" ? "default" : "ghost"}
              onClick={() => setActiveTab("weapons")}
              className="rounded-b-none text-xs h-7 px-2"
              size="sm"
            >
              Weapons
            </Button>
          )}
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {activeTab === "stats" ? (
            <UnitStats
              speed={unit.speed}
              roadSpeed={unit.roadSpeed}
              rotationTime={unit.rotationTime}
              optics={unit.optics}
              airOptics={unit.airOptics}
              lowAltOptics={unit.lowAltOptics}
              opticsRange={unit.opticsRange}
              airOpticsRange={unit.airOpticsRange}
              lowAltOpticsRange={unit.lowAltOpticsRange}
              stealth={unit.stealth}
              ecm={unit.ecm}
              fuel={unit.fuel}
              fuelMove={unit.fuelMove}
              supply={unit.supply}
              advancedDeployment={unit.advancedDeployment}
              maxDamage={unit.maxDamage}
              dangerousness={unit.dangerousness}
              agility={unit.agility}
              movementType={unit.movementType}
              flyingAltitude={unit.flyingAltitude}
              travelTime={unit.travelTime}
              bombStrategy={unit.bombStrategy}
            />
          ) : activeTab === "weapons" && hasWeapons ? (
            <div className="space-y-3">
              {loadingWeapons ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : weaponData.weapons.length > 1 ? (
                <>
                  {/* Weapon tabs */}
                  <div className="flex gap-2 border-b overflow-x-auto">
                    {weaponData.weapons.map((weapon, index) => (
                      <Button
                        key={weapon.salvoIndex}
                        variant={activeWeaponIndex === index ? "default" : "ghost"}
                        onClick={() => setActiveWeaponIndex(index)}
                        className="rounded-b-none whitespace-nowrap text-xs h-7 px-2"
                        size="sm"
                      >
                        {weapon.ammo.name} {weapon.numberOfWeapons > 1 && `x${weapon.numberOfWeapons}`}
                        <Badge variant="outline" className="ml-2 text-[10px]">S{weapon.salvoIndex}</Badge>
                      </Button>
                    ))}
                  </div>
                  {/* Active weapon card */}
                  {weaponData.weapons[activeWeaponIndex] && (
                    <WeaponCard
                      weapon={weaponData.weapons[activeWeaponIndex]!}
                      weaponIndex={activeWeaponIndex}
                    />
                  )}
                </>
              ) : weaponData.weapons[0] ? (
                <WeaponCard weapon={weaponData.weapons[0]} weaponIndex={0} />
              ) : null}
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

